import { useEffect, useState } from "react";
import { api, fmtPrice } from "../../lib/api";
import { useBusiness } from "../../context/BusinessContext";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X } from "lucide-react";

const empty = {
  name: "",
  price: 0,
  description: "",
  category_id: "",
  image_url: "",
  stock: 0,
  featured: false,
};

export default function AdminProducts() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const { business } = useBusiness();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const refresh = async () => {
    const [p, c] = await Promise.all([
      api.get("/products", { params: { limit: 100 } }),
      api.get("/categories"),
    ]);
    setItems(p.data.items);
    setCats(c.data);
  };

  useEffect(() => {
    refresh();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm({ ...empty, category_id: cats[0]?.id || "" });
    setOpen(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      name: p.name,
      price: p.price,
      description: p.description || "",
      category_id: p.category_id,
      image_url: p.image_url || "",
      stock: p.stock,
      featured: !!p.featured,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await api.put(`/products/${editing.id}`, form);
        toast.success("Product updated");
      } else {
        await api.post("/products", form);
        toast.success("Product created");
      }
      setOpen(false);
      refresh();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    await api.delete(`/products/${p.id}`);
    toast.success("Deleted");
    refresh();
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Image uploaded");
    } catch (err) {
      toast.error("Upload failed");
    }
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="text-xs uppercase tracking-ultra text-neutral-500">Catalogue</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter mt-2">Products</h1>
        </div>
        <button
          onClick={openCreate}
          className="bg-black text-white px-6 py-3 text-xs uppercase tracking-ultra font-bold inline-flex items-center gap-2"
          data-testid="admin-new-product-button"
        >
          <Plus className="w-4 h-4" /> New product
        </button>
      </div>

      <div className="mt-10 bg-white border border-black/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#0a0a0a] text-white">
            <tr>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-ultra">Name</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-ultra">Category</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-ultra">Price</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-ultra">Stock</th>
              <th className="text-left px-4 py-3 text-[10px] uppercase tracking-ultra">Featured</th>
              <th className="text-right px-4 py-3 text-[10px] uppercase tracking-ultra">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-black/10">
            {items.map((p) => (
              <tr key={p.id} data-testid={`admin-product-row-${p.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-neutral-200 overflow-hidden">
                      {p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}
                    </div>
                    <span className="font-medium">{p.name}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-neutral-500">{p.category_name}</td>
                <td className="px-4 py-3 text-right font-medium">
                  {fmtPrice(p.price, business.currency)}
                </td>
                <td className="px-4 py-3 text-right">{p.stock}</td>
                <td className="px-4 py-3">{p.featured ? "Yes" : "—"}</td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => openEdit(p)} className="p-2 hover:text-accent" data-testid={`edit-product-${p.id}`}>
                    <Pencil className="w-4 h-4" />
                  </button>
                  <button onClick={() => remove(p)} className="p-2 hover:text-accent" data-testid={`delete-product-${p.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            ))}
            {items.length === 0 && (
              <tr>
                <td colSpan="6" className="px-4 py-10 text-center text-neutral-500 text-sm">
                  No products yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-auto p-4" data-testid="product-form-modal">
          <div className="bg-white w-full max-w-2xl mt-10 p-8 border border-black">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-black tracking-tighter">
                {editing ? "Edit product" : "New product"}
              </h2>
              <button onClick={() => setOpen(false)} className="p-2" data-testid="product-form-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <form onSubmit={save} className="space-y-4">
              <Row label="Name" required>
                <input
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full border-b border-black bg-transparent py-2 text-sm outline-none"
                  data-testid="product-form-name"
                />
              </Row>
              <div className="grid grid-cols-2 gap-4">
                <Row label="Price" required>
                  <input
                    required
                    type="number"
                    step="0.01"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: parseFloat(e.target.value) || 0 })}
                    className="w-full border-b border-black bg-transparent py-2 text-sm outline-none"
                    data-testid="product-form-price"
                  />
                </Row>
                <Row label="Stock" required>
                  <input
                    required
                    type="number"
                    value={form.stock}
                    onChange={(e) => setForm({ ...form, stock: parseInt(e.target.value) || 0 })}
                    className="w-full border-b border-black bg-transparent py-2 text-sm outline-none"
                    data-testid="product-form-stock"
                  />
                </Row>
              </div>
              <Row label="Category" required>
                <select
                  required
                  value={form.category_id}
                  onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                  className="w-full border-b border-black bg-transparent py-2 text-sm outline-none"
                  data-testid="product-form-category"
                >
                  <option value="">Select...</option>
                  {cats.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>
              </Row>
              <Row label="Description">
                <textarea
                  rows={3}
                  value={form.description}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                  className="w-full border border-black/10 bg-white p-3 text-sm outline-none focus:border-black"
                  data-testid="product-form-description"
                />
              </Row>
              <Row label="Image">
                <input
                  value={form.image_url}
                  onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                  placeholder="https://..."
                  className="w-full border-b border-black bg-transparent py-2 text-sm outline-none"
                  data-testid="product-form-image-url"
                />
                <div className="mt-2 flex items-center gap-2">
                  <label className="text-[10px] uppercase tracking-ultra border border-black px-3 py-1 cursor-pointer hover:bg-black hover:text-white">
                    Upload
                    <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="product-form-image-upload" />
                  </label>
                  {form.image_url && <img src={form.image_url} alt="" className="w-12 h-12 object-cover" />}
                </div>
              </Row>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.featured}
                  onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                  data-testid="product-form-featured"
                />
                <span className="text-xs uppercase tracking-ultra">Featured</span>
              </label>
              <div className="flex gap-3 pt-4">
                <button type="submit" className="bg-black text-white px-6 py-3 text-xs uppercase tracking-ultra font-bold" data-testid="product-form-save">
                  Save
                </button>
                <button type="button" onClick={() => setOpen(false)} className="border border-black px-6 py-3 text-xs uppercase tracking-ultra font-bold">
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({ label, children, required }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-ultra text-neutral-500 mb-1">
        {label} {required && <span className="text-accent">*</span>}
      </label>
      {children}
    </div>
  );
}
